<template>
  <div
    class="layout grid bg-grey-900"
    :class="{ 'with-sidebar': hasSidebar, 'without-sidebar': !hasSidebar }"
  >
    <div
      class="topbar border-gray-200 shadow-xs top-0 flex h-14 shrink-0 items-center gap-x-4 bg-grey-900 px-3"
    >
      <div v-if="!hasSidebar" class="hidden h-14 shrink-0 items-center lg:flex">
        <NuxtLink to="/">
          <img
            class="h-7 w-auto"
            src="/assets/icons/layout/logo-white.png"
            alt="Benchmark Mineral Intelligence"
          />
        </NuxtLink>
      </div>

      <Topbar :navigation="topbarNavigation" @open-sidebar="openSidebar" />
    </div>

    <div
      v-if="hasSidebar"
      class="sidebar hidden lg:inset-y-0 lg:z-50 lg:flex lg:flex-col"
    >
      <slot name="sidebar" :topbar-navigation="topbarNavigation" />
    </div>

    <MobileSidebar
      :topbar-navigation="topbarNavigation"
      :navigation="navigation"
      :open="sidebarOpen"
      @close="closeSidebar"
    />

    <main
      id="main-container"
      :class="
        twMerge(
          'main rounded-t-lg',
          containsEmbed ? '' : 'bg-white px-6 pt-main-content-pt',
          mainClasses,
        )
      "
    >
      <div
        class="mr-auto max-w-content-container"
        :class="{ 'rounded-t-lg bg-s-200/5': containsEmbed }"
      >
        <slot name="contextual-nav" />
        <slot />
      </div>
    </main>

    <ContactModal />
    <CommonToast
      :show="toastStore.show"
      :position="toastStore.position"
      :type="toastStore.type"
      :message="toastStore.message"
      :duration="toastStore.duration"
      :icon="toastStore.icon"
      @close="toastStore.clearToast"
    />

    <ForgotPasswordModal v-if="getModal('forgotPassword')" />
    <SignInModal />
    <CreateAccountModal />
    <SubscribeModal v-if="getModal('subscribe')" />
    <ModalNewsletter v-if="getModal('newsletter')" />
    <CapexTrackerModal v-if="getModal('capexTracker')" />
    <CapexTrackerContactModal v-if="getModal('capexTrackerContact')" />
    <IndustryReportsModal v-if="getModal('industryReports')" />
    <RequestForProposalModal v-if="getModal('requestForProposal')" />
  </div>
</template>

<script setup lang="ts">
import Topbar from '~/components/layout/Topbar.vue'

import ContactModal from '~/components/contact/Modal.vue'
import ForgotPasswordModal from '~/components/ForgotPasswordModal.vue'
import SignInModal from '~/components/SignInModal.vue'
import CreateAccountModal from '~/components/CreateAccountModal.vue'
import ModalNewsletter from '~/components/shared/common/ModalNewsletter.vue'
import CapexTrackerModal from '~/components/CapexTrackerModal.vue'
import MobileSidebar from '~/components/layout/MobileSidebar.vue'
import SubscribeModal from '~/components/subscribe/Modal.vue'
import CapexTrackerContactModal from '~/components/CapexTrackerContactModal.vue'
import IndustryReportsModal from '~/components/IndustryReportsModal.vue'
import RequestForProposalModal from '~/components/RequestForProposalModal.vue'
import { useModalStore } from '~/stores/modal'
import { twMerge } from 'tailwind-merge'
import { useToastStore } from '~/stores/toast'

const { getModal } = useModalStore()
const toastStore = useToastStore()
const route = useRoute()
const containsEmbed = computed(() => route.meta.isEmbed)

defineProps<{
  mainClasses?: string
}>()

const sidebarOpen = ref(false)

const slots = useSlots()
const { topbarNavigation } = useTopbarNavigation()

const navigationStore = useNavigationStore()
const { navigation } = storeToRefs(navigationStore)

const hasSidebar = computed(() => slots.sidebar !== undefined)

const openSidebar = () => {
  sidebarOpen.value = true
}

const closeSidebar = () => {
  sidebarOpen.value = false
}
</script>

<style scoped>
.layout.without-sidebar {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
}

.layout.with-sidebar {
  grid-template-areas:
    'topbar topbar'
    'main main';
}

.layout .sidebar,
.layout .main {
  overflow-y: auto;
}

.layout.with-sidebar .sidebar {
  grid-area: sidenav;
  overflow-y: auto;
}

.layout.with-sidebar .topbar {
  grid-area: topbar;
}

.layout.with-sidebar .main {
  grid-area: main;
}

@media (width >= 64rem /* 1024px */) {
  .layout.with-sidebar {
    display: grid;
    grid-template:
      'sidenav topbar' auto
      'sidenav main' 1fr
      / 250px 1fr;
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
  }
}
</style>
